import { AuthProvider } from '@auth/useAuth'
import GainsightPxIdentifier from '@globals/GainsightPxIdentifier'
import Language from '@globals/Language'
import { useThemeStore } from '@globals/themes/useThemeStore'
import Timezone from '@globals/Timezone'
import { LanguagesEnum } from '@kudos/http-client'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { ErrorBoundary } from '@rollbar/react'
import { QueryClient, QueryClientConfig, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { injectFroalaContentStyles } from '@shared/forms/richTextEditorField/froalaContentStyles'
import AppLayout from './AppLayout'
import RollbarProvider from './RollbarProvider'
import RouteRenderer from './routes'
import ErrorBoundaryUI from './routes/errors/ErrorBoundaryUI'

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,

      // Data immediately considered stale. Developers can override this on a per-query basis
      // by specifying {staleTime: xxx} in the query options.
      staleTime: 0,
      // How long to keep the data in memory for before evicting it from the cache. Cached
      // data may be shown as placeholder while new data is being fetched.
      gcTime: 5 * (60 * 1000),
    },
  },
}

const queryClient: QueryClient = new QueryClient(queryClientConfig)

export const App = () => {
  const [languageKey, setLanguageKey] = useState<LanguagesEnum>(LanguagesEnum.ENGLISH)

  const getCurrentTheme = useThemeStore(state => state.getCurrentTheme)

  // Inject Froala content rendering styles globally once on app mount
  useEffect(() => {
    injectFroalaContentStyles()
  }, [])

  const handleLanguageChange = (locale: LanguagesEnum) => {
    setLanguageKey(locale)
  }

  return (
    <RollbarProvider>
      <ThemeProvider theme={getCurrentTheme()}>
        <CssBaseline />
        <ErrorBoundary fallbackUI={ErrorBoundaryUI}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <>
                {/* NOTE: Changes to the 'languageKey' prop would trigger App re-render */}
                <AppLayout key={languageKey} languageKey={languageKey}>
                  <RouteRenderer />
                </AppLayout>
                <Language onLanguageChange={handleLanguageChange} />
                <Timezone />
                <GainsightPxIdentifier />
              </>
            </AuthProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </RollbarProvider>
  )
}
